import { useState, useEffect, useRef, lazy, Suspense, Component, ErrorInfo, ReactNode, useMemo } from "react";
import { WelcomeScreen } from "./components/WelcomeScreen";
import {
  photos as defaultPhotos,
  printSizes,
} from "./data/photos";
import { Photo, CartItem, PrintSize } from "./types";
import { toast } from "sonner@2.0.3";
import { Toaster } from "sonner@2.0.3";
import {
  projectId,
  publicAnonKey,
} from "./utils/supabase/info";

// ── Core components — direct imports to avoid lazy-load failures in Figma Make ──
import Header from "./components/Header";
import Hero from "./components/Hero";
import PhotoGallery from "./components/PhotoGallery";
import About from "./components/About";
import GridGallery from "./components/GridGallery";
import PhotoDialog from "./components/PhotoDialog_Option2_LessPadding";
import CartSheet from "./components/CartSheet";
import CheckoutDialog from "./components/CheckoutDialog";
import PaymentSuccess from "./components/PaymentSuccess";
import CollectionsPage from "./components/CollectionsPage";
import CategoryOverview from "./components/CategoryOverview";

// ━━━ IN-HOUSE TEST MODE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Set to true to bypass Teya/Borgun and simulate the full checkout flow locally.
// Set to false when ready to test real payments.
const TEST_MODE = false;
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Error boundary to catch rendering crashes and show a diagnostic message
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null; errorInfo: ErrorInfo | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("🔴 ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ background: '#1a1a1a', color: '#facc15', minHeight: '100vh', padding: '2rem', fontFamily: 'monospace' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>⚠️ Rendering Error</h1>
          <pre style={{ color: '#ff6b6b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {this.state.error.toString()}
          </pre>
          <pre style={{ color: '#888', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: '1rem', fontSize: '0.75rem' }}>
            {this.state.errorInfo?.componentStack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Inline the types so we don't need `import type` from lazy-loaded modules
interface HeroConfig {
  mode: 'video' | 'images';
  videoUrl?: string | null;
  videoFileName?: string | null;
  audioUrl?: string | null;
  audioFileName?: string | null;
}

interface ShippingInfo {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

// Lazy-load ALL heavy components to reduce initial bundle
// Wrapper to log which module fails to import
function safeLazy(name: string, loader: () => Promise<any>) {
  return lazy(() => loader().catch(err => {
    console.error(`🔴 LAZY IMPORT FAILED: ${name}`, err);
    // Return a fallback component so the app doesn't crash
    return { default: () => <div style={{color:'red',padding:'1rem'}}>Failed to load: {name} — {String(err)}</div> };
  }));
}

// ── Admin-only components (safeLazy – rarely loaded) ──────────────────
const AdminAuth = safeLazy("AdminAuth", () => import("./components/AdminAuth").then(m => ({ default: m.AdminAuth })));
const AdminMenu = safeLazy("AdminMenu", () => import("./components/AdminMenu").then(m => ({ default: m.AdminMenu })));
const AdminMockup = safeLazy("AdminMockup", () => import("./components/AdminMockup").then(m => ({ default: m.AdminMockup })));
const PhotoManager = safeLazy("PhotoManager", () => import("./components/PhotoManager").then(m => ({ default: m.PhotoManager })));
const PrintDebugger = safeLazy("PrintDebugger", () => import("./components/PrintDebugger").then(m => ({ default: m.PrintDebugger })));
const AdminPanel = safeLazy("AdminPanel", () => import("./components/AdminPanel").then(m => ({ default: m.AdminPanel })));
const ImageDebugger = safeLazy("ImageDebugger", () => import("./components/ImageDebugger").then(m => ({ default: m.ImageDebugger })));
const CertificatePreview = safeLazy("CertificatePreview", () => import("./components/CertificatePreview").then(m => ({ default: m.CertificatePreview })));
const ThankYouPreview = safeLazy("ThankYouPreview", () => import("./components/ThankYouPreview").then(m => ({ default: m.ThankYouPreview })));
const AnimationShowcase = safeLazy("AnimationShowcase", () => import("./components/AnimationShowcase").then(m => ({ default: m.AnimationShowcase })));
const TestPaymentDialog = safeLazy("TestPaymentDialog", () => import("./components/TestPaymentDialog").then(m => ({ default: m.TestPaymentDialog })));

// Suspense fallback
const BlackFallback = <div className="min-h-screen bg-black" />;

export default function App() {
  // ─── Keyiscreation Photography Store ───
  const params = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const paymentStatus = params.get("payment");
  const orderIdFromUrl = params.get("orderId");
  const isPaymentSuccess = paymentStatus === "success" && !!orderIdFromUrl;
  const isPaymentCancelled = paymentStatus === "cancelled";
  const sessionIdFromUrl = params.get("session_id");
  const hasPaymentReturn = isPaymentSuccess || isPaymentCancelled || !!sessionIdFromUrl;
  const isMockupAdmin = params.get("mockup") === "admin";
  const previewMode = params.get("preview");

  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showGridGallery, setShowGridGallery] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showPhotoManager, setShowPhotoManager] = useState(false);
  const [showCertificateDemo, setShowCertificateDemo] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showDebugger, setShowDebugger] = useState(false);
  const [showPrintDebugger, setShowPrintDebugger] = useState(false);
  const [showThankYouPreview, setShowThankYouPreview] = useState(false);
  const [showAnimationShowcase, setShowAnimationShowcase] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showCollections, setShowCollections] = useState(false);
  const [showCategoryOverview, setShowCategoryOverview] = useState(false);
  const [photos, setPhotos] = useState<Photo[]>(defaultPhotos);
  const [loading, setLoading] = useState(!hasPaymentReturn);
  const [heroReady, setHeroReady] = useState(false);
  const [, setHeroConfigLoaded] = useState(false);
  const [currentPrintSizes, setCurrentPrintSizes] = useState<PrintSize[]>(printSizes);
  const [, setShippingPrice] = useState(0);
  const [, setFontSettings] = useState({
    fontFamily: "Inter",
    headingFont: "Playfair Display",
    baseFontSize: 16,
  });
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(() => {
    try {
      return sessionStorage.getItem('admin_authenticated') === 'true'
        && !!sessionStorage.getItem('admin_token');
    } catch { return false; }
  });
  const [adminToken, setAdminToken] = useState<string>(() => {
    try { return sessionStorage.getItem('admin_token') || ''; } catch { return ''; }
  });
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showMockup, setShowMockup] = useState(false);
  const [showTestPayment, setShowTestPayment] = useState(false);
  const [testPaymentAmount, setTestPaymentAmount] = useState(0);
  const footerClickCount = useRef(0);
  const footerClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const footerLongPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [heroConfig, setHeroConfig] = useState<HeroConfig>({ mode: 'images' });

  useEffect(() => {
    setHeroReady(false);
  }, [heroConfig.mode, heroConfig.videoUrl]);

  const serverUrl = `https://${projectId}.supabase.co/functions/v1/make-server-a5df78ef`;

  const calculateShipping = (_items: CartItem[]): number => 0;

  // Helper: fetch with retry for cold-start resilience
  const fetchWithRetry = async (url: string, options?: RequestInit, retries = 3, delay = 2000): Promise<Response> => {
    for (let i = 0; i <= retries; i++) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 45000);
        const response = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(timeout);
        return response;
      } catch (error) {
        if (i < retries) {
          console.log(`⏳ Fetch failed (attempt ${i + 1}/${retries + 1}), retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          delay *= 2;
        } else {
          throw error;
        }
      }
    }
    throw new Error('fetchWithRetry: unreachable');
  };

  // ─── Client-side cache helpers ──────────────────────────────────────
  const INIT_CACHE_KEY = 'kc_init_cache';
  const INIT_CACHE_TTL = 5 * 60 * 1000;

  const getInitCache = (): any | null => {
    try {
      const raw = sessionStorage.getItem(INIT_CACHE_KEY);
      if (!raw) return null;
      const { data, ts } = JSON.parse(raw);
      if (Date.now() - ts > INIT_CACHE_TTL) {
        sessionStorage.removeItem(INIT_CACHE_KEY);
        return null;
      }
      return data;
    } catch { return null; }
  };

  const setInitCache = (data: any) => {
    try {
      sessionStorage.setItem(INIT_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
    } catch { /* storage full */ }
  };

  const applyPricing = (pricingData: any): PrintSize[] => {
    if (!pricingData) return printSizes;
    const updatedSizes: PrintSize[] = [
      { id: "small", name: "Small", dimensions: "29.7 × 42.0 cm", dimensionsInches: "11.7 × 16.5 in", price: Number(pricingData.small) || 70, available: 10 },
      { id: "medium", name: "Medium", dimensions: "42.0 × 59.4 cm", dimensionsInches: "16.5 × 23.4 in", price: Number(pricingData.medium) || 100, available: 10 },
      { id: "large", name: "Large", dimensions: "84.1 × 118.9 cm", dimensionsInches: "33.1 × 46.8 in", price: Number(pricingData.large) || 120, available: 10 },
    ];
    setCurrentPrintSizes(updatedSizes);
    setShippingPrice(0);

    try {
      const newFontSettings = {
        fontFamily: pricingData.fontFamily || "Inter",
        headingFont: pricingData.headingFont || "Playfair Display",
        baseFontSize: Number(pricingData.baseFontSize) || 16,
      };
      setFontSettings(newFontSettings);
      if (typeof document !== "undefined" && document.documentElement) {
        document.documentElement.style.setProperty("--font-sans", `'${newFontSettings.fontFamily}', sans-serif`);
        document.documentElement.style.setProperty("--font-serif", `'${newFontSettings.headingFont}', serif`);
        document.documentElement.style.setProperty("--font-size", `${newFontSettings.baseFontSize}px`);
      }
    } catch (fontError) {
      console.error("Error applying font settings:", fontError);
    }
    return updatedSizes;
  };

  const applyHeroConfig = (config: any) => {
    if (config) {
      setHeroConfig({
        mode: config.mode || 'images',
        videoUrl: config.videoUrl || null,
        videoFileName: config.videoFileName || null,
        audioUrl: config.audioUrl || null,
        audioFileName: config.audioFileName || null,
      });
    }
    setHeroConfigLoaded(true);
  };

  const applyPhotos = (photosData: any[], sizes: PrintSize[]) => {
    // Test photo for Teya payment verification - remove after testing
    const testPhoto: Photo = {
      id: 'test-1',
      title: 'TEST - 1 EUR Payment Test',
      description: 'Test photo for verifying the Teya payment flow. Price is 1 EUR. No SKU so it will NOT trigger print fulfillment. Remove after testing.',
      imageUrl: 'https://images.unsplash.com/photo-1772050138916-559a186e6d7b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb2xvcmZ1bCUyMGFic3RyYWN0JTIwdGVzdCUyMHBhdHRlcm58ZW58MXx8fHwxNzczMzM1MzEwfDA&ixlib=rb-4.1.0&q=80&w=1080',
      category: 'street',
      orientation: 'square',
      sizes: [
        { id: 'test-small', name: 'Test Small (1 EUR)', dimensions: '29.7 x 42.0 cm', dimensionsInches: '11.7 x 16.5 in', price: 1, available: 99 },
      ]
    };

    if (photosData && photosData.length > 0) {
      // Normalize legacy category names to current IDs
      const normalizeCategory = (cat: string | undefined): string => {
        if (!cat) return 'street';
        const lower = cat.toLowerCase();
        if (lower === 'landscape' || lower === 'nature & landscape') return 'nature';
        if (lower === 'portrait' || lower === 'people') return 'portrait';
        if (lower === 'black & white' || lower === 'bw') return 'blackwhite';
        if (lower === 'street' || lower === 'street & graffiti') return 'street';
        // Already a valid ID
        if (['street', 'portrait', 'blackwhite', 'nature'].includes(lower)) return lower;
        return 'street';
      };

      const transformedPhotos: Photo[] = photosData.map((photo: any) => {
        const availableSizes = photo.sizes && photo.sizes.length > 0 ? photo.sizes : sizes;
        const rawCat = photo.category;
        const normalizedCat = normalizeCategory(rawCat);
        console.log(`🏷️ Photo "${photo.title}" raw="${rawCat}" → normalized="${normalizedCat}"`);
        return {
          id: photo.id,
          title: photo.title,
          description: photo.description || "Printed on high-quality paper with professional photography printing.",
          imageUrl: photo.imageUrl,
          thumbnailUrl: photo.thumbnailUrl,
          category: normalizedCat,
          orientation: photo.orientation || "landscape",
          sizes: availableSizes,
        };
      });

      // Summary by category
      const catCounts: Record<string, number> = {};
      transformedPhotos.forEach(p => { catCounts[p.category] = (catCounts[p.category] || 0) + 1; });
      console.log(`📊 Category counts:`, catCounts);

      setPhotos([testPhoto, ...transformedPhotos]);
    } else {
      setPhotos([testPhoto, ...defaultPhotos.map((photo) => ({ ...photo, sizes: [...sizes] }))]);
    }
  };

  const fetchInit = async (skipCache = false) => {
    try {
      if (!skipCache) {
        const cached = getInitCache();
        if (cached) {
          console.log('⚡ Using cached init data');
          const sizes = applyPricing(cached.pricing);
          applyHeroConfig(cached.heroConfig);
          applyPhotos(cached.photos, sizes);
          return;
        }
      }

      const initUrl = skipCache ? `${serverUrl}/init?fresh=1` : `${serverUrl}/init`;
      console.log(`🚀 Fetching ${initUrl}...`);
      const response = await fetchWithRetry(initUrl, {
        headers: { Authorization: `Bearer ${publicAnonKey}` },
      });

      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const data = await response.json();
      console.log(`✅ /init received: ${data.photos?.length || 0} photos`);

      setInitCache(data);
      const sizes = applyPricing(data.pricing);
      applyHeroConfig(data.heroConfig);
      applyPhotos(data.photos, sizes);
    } catch (error) {
      console.error("❌ Error in fetchInit:", error);
      if (error instanceof TypeError && error.message.includes('Load failed')) {
        toast.error("Cannot connect to server", {
          description: "Using default data.",
          duration: 5000,
        });
      }
      setPhotos(defaultPhotos);
      setHeroConfigLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const refreshAfterAdmin = () => {
    try { sessionStorage.removeItem(INIT_CACHE_KEY); } catch {}
    fetchInit(true);
  };

  useEffect(() => {
    if (previewMode) {
      const sampleItems: CartItem[] = [
        { photo: defaultPhotos[0], selectedSize: printSizes[1], quantity: 1 },
        { photo: defaultPhotos[1] || defaultPhotos[0], selectedSize: printSizes[0], quantity: 2 },
      ];
      setCartItems(sampleItems);
      setLoading(false);
      setHeroReady(true);

      if (previewMode === "checkout") setIsCheckoutOpen(true);
      else if (previewMode === "cart") setIsCartOpen(true);
      else if (previewMode === "dialog") {
        setSelectedPhoto(defaultPhotos[0]);
        setIsDialogOpen(true);
      } else if (previewMode === "success") setShowPaymentSuccess(true);
      return;
    }

    if (hasPaymentReturn) {
      console.log("✅ PAYMENT RETURN DETECTED - orderId:", orderIdFromUrl || sessionIdFromUrl);

      if (isPaymentCancelled) {
        console.log("❌ Payment was cancelled by user");
        setLoading(false);
        toast.info("Greiðslu hætt við", { description: "Þú getur reynt aftur hvenær sem er.", duration: 5000 });
        // Clean URL and load normally
        if (typeof window !== "undefined") {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, "", cleanUrl);
        }
        fetchInit(true);
        return;
      }

      if (isPaymentSuccess && orderIdFromUrl) {
        fetch(`${serverUrl}/teya-verify/${orderIdFromUrl}`, {
          headers: { Authorization: `Bearer ${publicAnonKey}` },
        })
          .then(r => r.json())
          .then(data => {
            console.log("🔍 Payment verification:", data);
            if (data.isPaid) console.log("✅ Payment confirmed!");
            else console.log("⏳ Payment not yet confirmed");
          })
          .catch(err => console.error("⚠️ Verification failed:", err));
      }

      setShowPaymentSuccess(true);
      setLoading(false);
      setCartItems([]);
      sessionStorage.removeItem("pendingOrder");
      toast.success("Pöntun móttekin!");

      if (typeof window !== "undefined") {
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, "", cleanUrl);
      }
      return;
    }

    if (!hasPaymentReturn) {
      // Force clear stale cache to ensure category normalization runs on fresh server data
      try { sessionStorage.removeItem(INIT_CACHE_KEY); } catch {}
      fetchInit(true); // skipCache=true to always fetch fresh from server
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "A") {
        e.preventDefault();
        if (isAdminAuthenticated) setShowAdminMenu(!showAdminMenu);
        else setShowAdminAuth(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Check stale pending orders
    const pendingOrder = sessionStorage.getItem("pendingOrder");
    if (pendingOrder) {
      try {
        const orderData = JSON.parse(pendingOrder);
        const timeSinceOrder = Date.now() - orderData.timestamp;
        if (timeSinceOrder < 10 * 60 * 1000) {
          fetch(`${serverUrl}/teya-verify/${orderData.orderId}`, {
            headers: { Authorization: `Bearer ${publicAnonKey}` },
          })
            .then(r => r.json())
            .then(data => {
              if (data.isPaid) {
                console.log("✅ Pending order was paid!");
                setShowPaymentSuccess(true);
                setCartItems([]);
                toast.success("Pöntun móttekin!");
              }
              sessionStorage.removeItem("pendingOrder");
            })
            .catch(() => sessionStorage.removeItem("pendingOrder"));
        } else {
          sessionStorage.removeItem("pendingOrder");
        }
      } catch (e) {
        console.error("Error parsing pending order:", e);
        sessionStorage.removeItem("pendingOrder");
      }
    }

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleViewDetails = (photo: Photo) => {
    setSelectedPhoto(photo);
    setIsDialogOpen(true);
  };

  const handleAddToCart = (photo: Photo, size: PrintSize) => {
    setCartItems((prev) => {
      const existingItem = prev.find(
        (item) => item.photo.id === photo.id && item.selectedSize.id === size.id,
      );
      if (existingItem) {
        if (existingItem.quantity >= size.available) {
          toast.error("Ekki fleiri tiltækar af þessari stærð");
          return prev;
        }
        toast.success("Uppfært í körfu");
        return prev.map((item) =>
          item.photo.id === photo.id && item.selectedSize.id === size.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      toast.success("Bætt í körfu");
      return [...prev, { photo, selectedSize: size, quantity: 1 }];
    });
  };

  const handleUpdateQuantity = (itemKey: string, delta: number) => {
    setCartItems((prev) =>
      prev.map((item) => {
        const key = `${item.photo.id}-${item.selectedSize.id}`;
        if (key === itemKey) {
          const newQuantity = Math.max(1, Math.min(item.selectedSize.available, item.quantity + delta));
          return { ...item, quantity: newQuantity };
        }
        return item;
      }),
    );
  };

  const handleRemoveItem = (itemKey: string) => {
    setCartItems((prev) =>
      prev.filter((item) => {
        const key = `${item.photo.id}-${item.selectedSize.id}`;
        return key !== itemKey;
      }),
    );
    toast.info("Fjarlægt úr körfu");
  };

  const handleBuyNow = (photo: Photo, size: PrintSize) => {
    console.log('🛒 BUY NOW clicked!', { photo: photo.title, size: size.name });
    setCartItems([{ photo, selectedSize: size, quantity: 1 }]);
    console.log('📋 Opening checkout dialog...');
    setIsCheckoutOpen(true);
    toast.info("Fer í kaup...");
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
  };

  const currentShippingPrice = calculateShipping(cartItems);

  const handleConfirmOrder = async (shippingInfo: ShippingInfo) => {
    console.log("🚀 Starting checkout process...", TEST_MODE ? "(TEST MODE)" : "(LIVE Teya)");

    // ━━━ TEST MODE: Simulate the full flow locally ━━━━━━━━━━━━━━━━━━━
    if (TEST_MODE) {
      try {
        setIsCheckoutOpen(false);
        const amount = cartItems.reduce((sum, i) => sum + i.selectedSize.price * i.quantity, 0);
        setTestPaymentAmount(amount);
        
        console.log("🧪 Test order prepared:");
        console.log("🧪 Items:", cartItems.map(i => `${i.photo.title} (${i.selectedSize.name}) x${i.quantity}`));
        console.log("🧪 Shipping to:", shippingInfo);
        console.log("🧪 Total: €" + amount);

        // Open the test payment dialog (simulated credit card form)
        setShowTestPayment(true);
      } catch (error) {
        console.error("❌ Test mode error:", error);
        toast.error("Villa í test mode", { id: "checkout" });
        setIsCheckoutOpen(true);
      }
      return;
    }

    // ━━━ LIVE MODE: Teya Hosted Checkout ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    try {
      setIsCheckoutOpen(false);
      toast.loading("Undirbý greiðslu...", { id: "checkout" });

      const response = await fetch(`${serverUrl}/create-teya-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({
          items: cartItems.map((item) => ({
            photoId: item.photo.id,
            photoTitle: item.photo.title,
            imageUrl: item.photo.imageUrl,
            sizeId: item.selectedSize.id,
            sizeName: item.selectedSize.name,
            price: item.selectedSize.price,
            quantity: item.quantity,
          })),
          shipping: {
            firstName: shippingInfo.firstName,
            lastName: shippingInfo.lastName,
            address: shippingInfo.address,
            city: shippingInfo.city,
            postalCode: shippingInfo.postalCode,
            country: shippingInfo.country,
          },
          customerEmail: shippingInfo.email,
          successUrl: window.location.origin,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Response error:", errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log("💳 Teya Hosted Checkout response:", data);

      if (data.success && data.sessionUrl) {
        // Store pending order for verification on return
        sessionStorage.setItem("pendingOrder", JSON.stringify({
          orderId: data.orderId,
          sessionId: data.sessionId,
          timestamp: Date.now(),
          cartItems: cartItems.length,
        }));

        const isInIframe = window.self !== window.top;

        if (isInIframe) {
          // IFRAME MODE: Open Teya checkout in new tab + poll for completion
          console.log("🖼️ Iframe detected — opening Teya Hosted Checkout in new tab");
          window.open(data.sessionUrl, "_blank");

          const orderId = data.orderId;
          let pollCount = 0;
          const maxPolls = 120;
          const pollInterval = setInterval(async () => {
            pollCount++;
            if (pollCount > maxPolls) {
              clearInterval(pollInterval);
              toast.dismiss("checkout-poll");
              return;
            }
            try {
              const verifyRes = await fetch(`${serverUrl}/teya-verify/${orderId}`, {
                headers: { Authorization: `Bearer ${publicAnonKey}` },
              });
              const verifyData = await verifyRes.json();
              if (verifyData.isPaid) {
                clearInterval(pollInterval);
                console.log("✅ Payment confirmed via polling!");
                toast.dismiss("checkout-poll");
                setShowPaymentSuccess(true);
                setCartItems([]);
                sessionStorage.removeItem("pendingOrder");
                toast.success("Greiðsla móttekin!", { duration: 5000 });
              }
            } catch (err) {
              console.log("⏳ Poll attempt", pollCount, "- waiting...", err);
            }
          }, 5000);

          toast.loading("Bíð eftir greiðslu í nýjum glugga...", { id: "checkout-poll", duration: Infinity });
        } else {
          // STANDALONE MODE: Simple redirect to Teya Hosted Checkout page
          console.log("🌐 Redirecting to Teya Hosted Checkout:", data.sessionUrl);
          toast.success("Fer á greiðslusíðu...", { id: "checkout", duration: 3000 });
          window.location.href = data.sessionUrl;
        }
      } else {
        throw new Error(data.error || "Failed to create Teya checkout session");
      }
    } catch (error) {
      console.error("❌ Error creating Teya checkout:", error);
      toast.error("Villa við að opna greiðslu", {
        description: error instanceof Error ? error.message : String(error),
        id: "checkout",
      });
      setIsCheckoutOpen(true);
      throw error;
    }
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Photos visible to the public: only those with at least one SKU-linked size
  const publicPhotos = useMemo(() =>
    photos.filter(p => p.sizes.some(s => s.sku)),
    [photos]
  );

  // Admin helpers
  const adminHeaders = () => ({
    Authorization: `Bearer ${publicAnonKey}`,
    'X-Admin-Token': adminToken,
  });

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
    setAdminToken('');
    setShowAdminMenu(false);
    setShowPhotoManager(false);
    setShowAdminPanel(false);
    setShowDebugger(false);
    setShowPrintDebugger(false);
    setShowCertificateDemo(false);
    setShowThankYouPreview(false);
    setShowAnimationShowcase(false);
    setShowMockup(false);
    try {
      sessionStorage.removeItem('admin_authenticated');
      sessionStorage.removeItem('admin_token');
    } catch {}
    toast.info("Admin útskráning");
  };

  // Footer tap-to-unlock: 5 rapid taps opens admin auth
  const handleFooterTap = () => {
    footerClickCount.current += 1;
    if (footerClickTimer.current) clearTimeout(footerClickTimer.current);
    if (footerClickCount.current >= 3) {
      footerClickCount.current = 0;
      if (isAdminAuthenticated) {
        setShowAdminMenu(true);
      } else {
        setShowAdminAuth(true);
      }
    } else {
      footerClickTimer.current = setTimeout(() => {
        footerClickCount.current = 0;
      }, 3000);
    }
  };

  // Long-press on copyright text — alternative mobile trigger (hold 1.5s)
  const handleCopyrightTouchStart = () => {
    footerLongPressTimer.current = setTimeout(() => {
      footerLongPressTimer.current = null;
      if (isAdminAuthenticated) {
        setShowAdminMenu(true);
      } else {
        setShowAdminAuth(true);
      }
    }, 1500);
  };
  const handleCopyrightTouchEnd = () => {
    if (footerLongPressTimer.current) {
      clearTimeout(footerLongPressTimer.current);
      footerLongPressTimer.current = null;
    }
  };

  // Show admin mockup if ?mockup=admin
  if (isMockupAdmin) {
    return (
      <Suspense fallback={BlackFallback}>
        <AdminMockup />
      </Suspense>
    );
  }

  return (
    <ErrorBoundary>
    <div className="relative min-h-screen bg-black">
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#1a1a1a', color: '#facc15', border: '1px solid #333' },
        }}
      />

      {/* Welcome/Loading overlay */}
      {!hasPaymentReturn && (
        <WelcomeScreen loading={loading} heroReady={heroReady} />
      )}

      {/* TEST MODE banner */}
      {TEST_MODE && (
        <div className="fixed top-0 left-0 right-0 z-[9998] bg-yellow-400 text-black text-center text-xs font-bold py-1 tracking-widest">
          TEST MODE — Greiðslur eru hermdar locally, ekki sent á Teya/Borgun
        </div>
      )}

      <Suspense fallback={BlackFallback}>
        {showPaymentSuccess ? (
          <PaymentSuccess
            onContinue={() => {
              setShowPaymentSuccess(false);
              setShowGridGallery(false);
              setShowAbout(false);
            }}
          />
        ) : showAbout ? (
          <>
            <Header
              transparent={true}
              cartItemCount={totalItems}
              onCartClick={() => setIsCartOpen(true)}
              onGalleryClick={() => { setShowCategoryOverview(true); setShowCollections(false); setShowAbout(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
              onHomeClick={() => {
                setShowAbout(false);
                setShowGridGallery(false);
                setShowCollections(false);
                setShowCategoryOverview(false);
                setSelectedCategory(null);
              }}
              onAboutClick={() => setShowAbout(true)}
              onPrintsClick={() => { setShowAbout(false); setShowCollections(true); setShowCategoryOverview(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
            />
            <About />
          </>
        ) : showCategoryOverview && !showGridGallery ? (
          <>
            <Header
              transparent={true}
              cartItemCount={totalItems}
              onCartClick={() => setIsCartOpen(true)}
              onGalleryClick={() => { setShowCategoryOverview(true); setShowCollections(false); setShowGridGallery(false); setSelectedCategory(null); }}
              onHomeClick={() => {
                setShowGridGallery(false);
                setShowAbout(false);
                setShowCollections(false);
                setShowCategoryOverview(false);
                setSelectedCategory(null);
              }}
              onAboutClick={() => { setShowAbout(true); setShowCategoryOverview(false); }}
              onPrintsClick={() => { setShowCollections(true); setShowCategoryOverview(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
            />
            <CategoryOverview
              onCategoryClick={(category) => {
                setShowCategoryOverview(false);
                setShowGridGallery(true);
                setSelectedCategory(category);
                window.scrollTo(0, 0);
              }}
              photos={publicPhotos}
            />
          </>
        ) : showCollections && !showGridGallery ? (
          <>
            <Header
              transparent={true}
              cartItemCount={totalItems}
              onCartClick={() => setIsCartOpen(true)}
              onGalleryClick={() => { setShowCategoryOverview(true); setShowCollections(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
              onHomeClick={() => {
                setShowGridGallery(false);
                setShowAbout(false);
                setShowCollections(false);
                setShowCategoryOverview(false);
                setSelectedCategory(null);
              }}
              onAboutClick={() => { setShowAbout(true); setShowCollections(false); }}
              onPrintsClick={() => { setShowCollections(true); setShowCategoryOverview(false); setShowGridGallery(false); setSelectedCategory(null); }}
            />
            <CollectionsPage
              photos={publicPhotos}
              printSizes={currentPrintSizes}
              onViewDetails={handleViewDetails}
              onAddToCart={handleAddToCart}
              onCategoryClick={(category) => {
                setShowCollections(false);
                setShowGridGallery(true);
                setSelectedCategory(category);
                window.scrollTo(0, 0);
              }}
            />
          </>
        ) : !showGridGallery ? (
          <div className="relative m-0 p-0">
            <section id="hero" className="m-0 p-0">
              <Hero
                onExploreClick={() => { setShowCategoryOverview(true); setShowCollections(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
                photos={publicPhotos}
                cartItemCount={totalItems}
                onCartClick={() => setIsCartOpen(true)}
                onGalleryClick={() => { setShowCategoryOverview(true); setShowCollections(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
                onHomeClick={() => {
                  setShowGridGallery(false);
                  setShowAbout(false);
                  setShowCollections(false);
                  setShowCategoryOverview(false);
                  setSelectedCategory(null);
                }}
                onAboutClick={() => setShowAbout(true)}
                onPrintsClick={() => { setShowCollections(true); setShowCategoryOverview(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
                heroConfig={heroConfig}
                onReady={() => setHeroReady(true)}
              />
            </section>

            <section
              id="squish"
              className="relative -mt-[140svh] pt-[calc(140svh-12rem)] m-0 p-0 -translate-y-px"
            >
              <div className="absolute top-[calc(140svh-12rem-4rem)] left-0 right-0 h-16 bg-gradient-to-b from-transparent to-black pointer-events-none z-10" />
              <PhotoGallery
                photos={publicPhotos}
                onViewDetails={handleViewDetails}
              />
            </section>

            {showPrintDebugger && (
              <div className="container mx-auto px-8">
                <Suspense fallback={null}>
                  <PrintDebugger onClose={() => setShowPrintDebugger(false)} />
                </Suspense>
              </div>
            )}
          </div>
        ) : (
          <>
            <Header
              transparent={true}
              cartItemCount={totalItems}
              onCartClick={() => setIsCartOpen(true)}
              onGalleryClick={() => { setShowCategoryOverview(true); setShowCollections(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
              onHomeClick={() => {
                setShowGridGallery(false);
                setShowAbout(false);
                setShowCollections(false);
                setShowCategoryOverview(false);
                setSelectedCategory(null);
              }}
              onAboutClick={() => { setShowAbout(true); setShowGridGallery(false); }}
              onPrintsClick={() => { setShowCollections(true); setShowCategoryOverview(false); setShowGridGallery(false); setSelectedCategory(null); window.scrollTo(0, 0); }}
            />
            <GridGallery
              photos={(() => {
                const filtered = selectedCategory ? publicPhotos.filter(p => p.category === selectedCategory) : publicPhotos;
                console.log(`🔍 GridGallery filter: selectedCategory="${selectedCategory}", total=${publicPhotos.length}, filtered=${filtered.length}`);
                if (selectedCategory) {
                  console.log(`🔍 Photos in "${selectedCategory}":`, filtered.map(p => p.title));
                  console.log(`🔍 All photo categories:`, publicPhotos.map(p => `${p.title}→${p.category}`));
                }
                return filtered;
              })()}
              onViewDetails={handleViewDetails}
              onBack={() => {
                setShowGridGallery(false);
                setShowCategoryOverview(true);
                setShowCollections(false);
                setSelectedCategory(null);
                window.scrollTo(0, 0);
              }}
              categoryTitle={
                selectedCategory === 'street' ? 'Street & Graffiti' :
                selectedCategory === 'portrait' ? 'People' :
                selectedCategory === 'blackwhite' ? 'Black & White' :
                selectedCategory === 'nature' ? 'Nature & Landscape' :
                undefined
              }
            />
            {showPrintDebugger && (
              <div className="container mx-auto px-8 mt-8">
                <Suspense fallback={null}>
                  <PrintDebugger onClose={() => setShowPrintDebugger(false)} />
                </Suspense>
              </div>
            )}
          </>
        )}

        <PhotoDialog
          photo={selectedPhoto}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onAddToCart={handleAddToCart}
          onBuyNow={handleBuyNow}
        />

        <CartSheet
          open={isCartOpen}
          onOpenChange={setIsCartOpen}
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onCheckout={handleCheckout}
          shippingPrice={currentShippingPrice}
        />

        <CheckoutDialog
          open={isCheckoutOpen}
          onOpenChange={setIsCheckoutOpen}
          cartItems={cartItems}
          onConfirmOrder={handleConfirmOrder}
          shippingPrice={currentShippingPrice}
        />

        {/* Test Payment Dialog (only in TEST_MODE) */}
        {TEST_MODE && (
          <TestPaymentDialog
            open={showTestPayment}
            onOpenChange={setShowTestPayment}
            totalAmount={testPaymentAmount}
            onPaymentSuccess={() => {
              setShowTestPayment(false);
              toast.success("🧪 TEST: Greiðsla samþykkt!", { duration: 3000 });
              setShowPaymentSuccess(true);
              setCartItems([]);
              toast.success("🧪 TEST: Pöntun móttekin!");
            }}
            onCancel={() => {
              setShowTestPayment(false);
              setIsCheckoutOpen(true);
            }}
          />
        )}

        {/* Admin overlays */}
        {showPhotoManager && (
          <Suspense fallback={BlackFallback}>
            <PhotoManager onClose={() => { setShowPhotoManager(false); refreshAfterAdmin(); }} adminToken={adminToken} />
          </Suspense>
        )}

        {showAdminPanel && (
          <Suspense fallback={BlackFallback}>
            <AdminPanel onClose={() => { setShowAdminPanel(false); refreshAfterAdmin(); }} adminToken={adminToken} />
          </Suspense>
        )}

        {showDebugger && (
          <Suspense fallback={BlackFallback}>
            <ImageDebugger onClose={() => setShowDebugger(false)} />
          </Suspense>
        )}

        {showCertificateDemo && (
          <Suspense fallback={BlackFallback}>
            <CertificatePreview
              open={showCertificateDemo}
              onOpenChange={setShowCertificateDemo}
              photo={defaultPhotos[0]}
              size={printSizes[0]}
              editionNumber={1}
            />
          </Suspense>
        )}

        {showThankYouPreview && (
          <Suspense fallback={BlackFallback}>
            <div className="fixed inset-0 z-50 bg-black">
              <ThankYouPreview />
              <button
                onClick={() => setShowThankYouPreview(false)}
                className="fixed top-4 right-4 z-[60] text-white bg-black/50 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </Suspense>
        )}

        {showAnimationShowcase && (
          <Suspense fallback={BlackFallback}>
            <AnimationShowcase onBack={() => setShowAnimationShowcase(false)} />
          </Suspense>
        )}

        {showMockup && (
          <Suspense fallback={BlackFallback}>
            <div className="fixed inset-0 z-50">
              <AdminMockup />
              <button
                onClick={() => setShowMockup(false)}
                className="fixed top-4 right-4 z-[60] text-white bg-black/50 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </Suspense>
        )}
      </Suspense>

      {/* Admin Auth Dialog */}
      {showAdminAuth && (
        <Suspense fallback={null}>
          <AdminAuth
            open={showAdminAuth}
            onOpenChange={setShowAdminAuth}
            onAuthenticated={(token: string) => {
              setIsAdminAuthenticated(true);
              setAdminToken(token);
              setShowAdminAuth(false);
              setShowAdminMenu(true);
              try {
                sessionStorage.setItem('admin_authenticated', 'true');
                sessionStorage.setItem('admin_token', token);
              } catch {}
              toast.success("Admin innskráning tókst!");
            }}
          />
        </Suspense>
      )}

      {/* Admin Menu */}
      {isAdminAuthenticated && showAdminMenu && (
        <Suspense fallback={null}>
          <AdminMenu
            onPhotoManager={() => { setShowAdminMenu(false); setShowPhotoManager(true); }}
            onAdminPanel={() => { setShowAdminMenu(false); setShowAdminPanel(true); }}
            onCertificate={() => { setShowAdminMenu(false); setShowCertificateDemo(true); }}
            onDebugger={() => { setShowAdminMenu(false); setShowDebugger(true); }}
            onPrintDebugger={() => { setShowAdminMenu(false); setShowPrintDebugger(true); }}
            onThankYouPreview={() => { setShowAdminMenu(false); setShowThankYouPreview(true); }}
            onAnimationShowcase={() => { setShowAdminMenu(false); setShowAnimationShowcase(true); }}
            onMockup={() => { setShowAdminMenu(false); setShowMockup(true); }}
            onLogout={handleAdminLogout}
            onClose={() => setShowAdminMenu(false)}
          />
        </Suspense>
      )}

      {/* Footer */}
      <footer className="bg-black text-center py-16 px-6">
        <h3
          className="text-white text-xl tracking-[0.5px] mb-4"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
        >
          Keyiscreation
        </h3>
        <p
          className="text-white/50 text-xs tracking-[0.4px] max-w-md mx-auto mb-6 leading-relaxed"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
        >
          Beautiful limited edition photographic prints. Each image comes with a
          certificate of authenticity and edition number.
        </p>
        <p
          className="text-yellow-400/70 text-xs italic mb-6 tracking-[0.3px]"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          In memory of my friend Þorleifur
        </p>
        <a
          href="https://www.instagram.com/keyiscreation"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-yellow-400 text-xs hover:text-yellow-300 transition-colors mb-4"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
          @keyiscreation
        </a>
        <div className="text-white/30 text-[11px] tracking-[0.5px] mt-2"
          onClick={handleFooterTap}
          onTouchStart={handleCopyrightTouchStart}
          onTouchEnd={handleCopyrightTouchEnd}
          onTouchCancel={handleCopyrightTouchEnd}
          style={{ userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', padding: '12px 24px', cursor: 'pointer' }}
        >© 2025</div>
        <div className="max-w-xs mx-auto mt-8 border-t border-white/10" />
        <div
          className="max-w-[8rem] md:max-w-[3rem] mx-auto mt-4 cursor-pointer group relative"
          onClick={handleFooterTap}
          role="button"
          tabIndex={-1}
        >
          <div className="h-px bg-white/15 group-hover:bg-white/30 transition-colors pointer-events-none" />
          <div className="h-8 md:h-4" />
        </div>
      </footer>
    </div>
    </ErrorBoundary>
  );
}